import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, MoreVerticalIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ContactsPagination } from '@/components/contacts/ContactsPagination';

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  accessor?: keyof T | ((row: T) => React.ReactNode);
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
  render?: (value: any, row: T, index: number) => React.ReactNode;
}

export interface RowAction {
  label: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  separatorBefore?: boolean;
  hidden?: boolean;
}

export const CRM_ROW_ACTIONS_TRIGGER_CLASS =
  'h-7 w-7 rounded-md text-[#98A2B3] hover:bg-[#F9FAFB] hover:text-[#101828]';
/*
 * The card-theme sticky column recipe, named so a table that is NOT built on
 * DataTable can wear the same one.
 *
 * 🔴 THESE ARE THE DEFINITIONS DataTable ITSELF USES — see stickySelectTd /
 * stickyFirstTh / stickyActionsTd below. The Meta Ads tables are hand-rolled
 * markup (column management, per-cell tooltips, sort heads) rather than
 * DataTable instances, and copying the class strings into that file would
 * leave two recipes to keep in step. The left OFFSET is not baked in: the
 * first data column sits at `left-12` behind a checkbox column and `left-0`
 * without one, so the caller composes it.
 *
 * A sticky cell needs an opaque background or the scrolled columns show
 * through it, and the row's hover colour has to be repeated here for the same
 * reason — which is why every row carrying these must also be a `group`.
 */
export const CARD_THEME_STICKY_SELECT_TH =
  'w-12 sticky left-0 z-30 border-0 bg-[#FAFAFB] px-3.5 py-3';
export const CARD_THEME_STICKY_SELECT_TD =
  'sticky left-0 z-10 px-3.5 py-3.5 border-0 bg-white transition-colors group-hover:bg-[#F9FAFB]';
export const CARD_THEME_STICKY_FIRST_TH = 'sticky z-30 bg-[#FAFAFB]';
export const CARD_THEME_STICKY_FIRST_TD =
  'sticky z-10 bg-white transition-colors group-hover:bg-[#F9FAFB]';
export const CARD_THEME_STICKY_ACTIONS_TH = 'sticky right-0 z-30 bg-[#FAFAFB] text-right';
export const CARD_THEME_STICKY_ACTIONS_TD =
  'sticky right-0 z-10 px-3.5 py-3.5 text-right border-0 bg-white transition-colors group-hover:bg-[#F9FAFB]';

export const CRM_ROW_ACTIONS_CONTENT_CLASS =
  'w-[220px] rounded-xl border border-[#E4E7EC] bg-white p-1.5 shadow-[0_8px_20px_rgba(16,24,40,0.08)]';
/**
 * The row-actions menu at a width suited to SHORT action lists.
 *
 * 🔴 A SEPARATE CONSTANT RATHER THAN NARROWING THE ONE ABOVE. That one is used
 * by fifteen files, including CRM tables whose items read "Convert to
 * customer"; taking 40px off it would wrap their labels. This is for menus
 * whose longest item is something like "Open in Meta".
 */
export const CRM_ROW_ACTIONS_CONTENT_COMPACT_CLASS =
  'w-[180px] rounded-xl border border-[#E4E7EC] bg-white p-1.5 shadow-[0_8px_20px_rgba(16,24,40,0.08)]';
export const CRM_ROW_ACTIONS_ITEM_CLASS =
  'cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-[#101828] hover:bg-[#F9FAFB] focus:bg-[#F9FAFB] focus:text-[#101828]';
export const CRM_ROW_ACTIONS_DESTRUCTIVE_ITEM_CLASS =
  'cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-[#B42318] hover:bg-[#FEF3F2] focus:bg-[#FEF3F2] focus:text-[#B42318]';
export const CRM_ROW_ACTIONS_SEPARATOR_CLASS = 'my-1 bg-[#F2F4F7]';

function RowActionsCell({
  actions,
  menuStyle = 'default',
}: {
  actions: RowAction[];
  menuStyle?: 'default' | 'crm';
}) {
  const visible = actions.filter((a) => !a.hidden);
  if (visible.length === 0) return null;
  const isCrm = menuStyle === 'crm';
  return (
    <div
      className="flex justify-end"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn('h-7 w-7 p-0', isCrm && CRM_ROW_ACTIONS_TRIGGER_CLASS)}
            aria-label="Row actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={isCrm ? 6 : undefined}
          className={cn('w-44', isCrm && CRM_ROW_ACTIONS_CONTENT_CLASS)}
        >
          {visible.map((action, index) => (
            <React.Fragment key={index}>
              {action.separatorBefore && index > 0 && (
                <DropdownMenuSeparator className={cn(isCrm && CRM_ROW_ACTIONS_SEPARATOR_CLASS)} />
              )}
              <DropdownMenuItem
                className={cn(
                  'gap-2',
                  action.destructive && 'text-red-600 focus:text-red-600',
                  isCrm &&
                    (action.destructive
                      ? CRM_ROW_ACTIONS_DESTRUCTIVE_ITEM_CLASS
                      : CRM_ROW_ACTIONS_ITEM_CLASS),
                )}
                disabled={action.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick?.();
                }}
              >
                {action.icon}
                {action.label}
              </DropdownMenuItem>
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function createActionsColumn<T>(
  getActions: (row: T, index: number) => RowAction[],
  options?: {
    header?: React.ReactNode;
    key?: string;
    className?: string;
    headerClassName?: string;
    menuStyle?: 'default' | 'crm';
  },
): Column<T> {
  return {
    key: options?.key ?? 'actions',
    header: options?.header ?? 'Actions',
    headerClassName: cn('text-right', options?.headerClassName),
    className: cn('text-right', options?.className),
    render: (_value, row, index) => (
      <RowActionsCell actions={getActions(row, index)} menuStyle={options?.menuStyle} />
    ),
  };
}

export interface MobileCardContext {
  index: number;
  selected: boolean;
  selectable: boolean;
  onSelectedChange: (checked: boolean) => void;
  actionsMenu: React.ReactNode;
  onClick?: () => void;
  cell: (key: string) => React.ReactNode;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  renderMobileCard?: (row: T, ctx: MobileCardContext) => React.ReactNode;
  data: T[];
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  totalItems?: number;
  loading?: boolean;
  emptyMessage?: React.ReactNode;
  className?: string;
  onRowClick?: (row: T, index: number) => void;
  selectable?: boolean;
  onSelectionChange?: (selectedRows: T[]) => void;
  isRowSelectable?: (row: T, index: number) => boolean;
  showPagination?: boolean;
  pageSizeOptions?: number[];
  innerScroll?: boolean;
  bodyFillsParent?: boolean;
  stickyHeader?: boolean;
  stickyFirstColumn?: boolean;
  fillParent?: boolean;
  paginationInScroll?: boolean;
  proxyHorizontalScroll?: boolean;
  virtualize?: boolean;
  virtualRowHeight?: number;
  selectionVersion?: number;
  selectedIds?: string[];
  selectedRowIds?: Set<string> | string[];
  getRowId?: (row: T, index: number) => string;
  rowActions?: (row: T, index: number) => RowAction[];
  variant?: 'default' | 'cardTheme';
  surface?: 'plain' | 'card';
  rowActionsMenuStyle?: 'default' | 'crm';
  scrollbars?: 'hidden' | 'visible';
  paginationStyle?: 'default' | 'crm';
  paginationItemLabel?: string;
  /** See ContactsPagination: 'sliding' shows three numbers that follow the user. */
  paginationWindow?: 'anchored' | 'sliding';
  /** Keep the pagination footer visible even when every row fits on one page. */
  alwaysShowPagination?: boolean;
}

function DataTable<T extends Record<string, any>>({
  columns: inputColumns,
  data,
  pageSize: controlledPageSize,
  currentPage: controlledPage,
  onPageChange,
  onPageSizeChange,
  totalItems,
  loading,
  emptyMessage = 'No data available',
  className,
  onRowClick,
  selectable = false,
  onSelectionChange,
  isRowSelectable,
  showPagination = true,
  pageSizeOptions = [10, 20], // Default limited to 10 and 20
  innerScroll = true,
  bodyFillsParent = false,
  stickyHeader = false,
  fillParent = false,
  paginationInScroll = false,
  stickyFirstColumn = false,
  proxyHorizontalScroll = false,
  virtualize = false,
  virtualRowHeight = 52,
  selectionVersion = 0,
  selectedIds,
  selectedRowIds,
  getRowId,
  rowActions,
  variant = 'default',
  rowActionsMenuStyle = 'default',
  surface = 'plain',
  scrollbars = 'hidden',
  paginationStyle = 'default',
  paginationItemLabel = 'results',
  paginationWindow = 'anchored',
  alwaysShowPagination = false,
  renderMobileCard,
}: DataTableProps<T>) {
  const isCardTheme = variant === 'cardTheme';
  const isMobile = useIsMobile();
  void paginationStyle;
  const columns = useMemo<Column<T>[]>(() => {
    if (!rowActions) return inputColumns;
    if (inputColumns.some((c) => c.key === 'actions')) return inputColumns;
    return [...inputColumns, createActionsColumn<T>(rowActions, { menuStyle: rowActionsMenuStyle })];
  }, [inputColumns, rowActions, rowActionsMenuStyle]);

  const [internalPage, setInternalPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(Math.max(10, controlledPageSize ?? 10));
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const prevSelectionVersion = useRef(selectionVersion);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const isControlledSelection = selectedRowIds !== undefined;
  const selectedIdSet = useMemo<Set<string>>(
    () => (selectedRowIds === undefined
      ? new Set<string>()
      : selectedRowIds instanceof Set
        ? selectedRowIds
        : new Set(selectedRowIds)),
    [selectedRowIds]
  );

  useEffect(() => {
    if (isControlledSelection) return;
    if (prevSelectionVersion.current !== selectionVersion) {
      prevSelectionVersion.current = selectionVersion;
      setSelectedRows(new Set());
    }
  }, [selectionVersion]);

  useEffect(() => {
    if (!selectedIds || !selectable) return;
    const idSet = new Set(selectedIds);
    const newSelected = new Set<number>();
    data.forEach((row, idx) => {
      if ((row as any).id && idSet.has((row as any).id)) {
        newSelected.add(idx);
      }
    });
    setSelectedRows(newSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedIds, selectable]);

  const virtualScrollRef = useRef<HTMLDivElement>(null);
  const proxyScrollActive = proxyHorizontalScroll && innerScroll && paginationInScroll;
  const proxyScrollRef = useRef<HTMLDivElement>(null);
  const [proxyScrollWidth, setProxyScrollWidth] = useState(0);
  const [proxyScrollOverflows, setProxyScrollOverflows] = useState(false);
  const proxySyncSource = useRef<'body' | 'proxy' | null>(null);

  useEffect(() => {
    if (!proxyScrollActive) return;
    const body = virtualScrollRef.current;
    if (!body) return;
    const measure = () => {
      setProxyScrollWidth(body.scrollWidth);
      setProxyScrollOverflows(body.scrollWidth > body.clientWidth + 1);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    const table = body.querySelector('table');
    if (table) observer.observe(table);
    return () => observer.disconnect();
  }, [proxyScrollActive, columns, data, loading]);

  const handleBodyScrollSync = () => {
    if (!proxyScrollActive) return;
    if (proxySyncSource.current === 'proxy') {
      proxySyncSource.current = null;
      return;
    }
    const body = virtualScrollRef.current;
    const proxy = proxyScrollRef.current;
    if (!body || !proxy || proxy.scrollLeft === body.scrollLeft) return;
    proxySyncSource.current = 'body';
    proxy.scrollLeft = body.scrollLeft;
  };

  const handleProxyScrollSync = () => {
    if (proxySyncSource.current === 'body') {
      proxySyncSource.current = null;
      return;
    }
    const body = virtualScrollRef.current;
    const proxy = proxyScrollRef.current;
    if (!body || !proxy || body.scrollLeft === proxy.scrollLeft) return;
    proxySyncSource.current = 'proxy';
    body.scrollLeft = proxy.scrollLeft;
  };

  const handleBodyWheelSync = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!proxyScrollActive || !event.deltaX) return;
    const body = virtualScrollRef.current;
    if (!body || body.scrollWidth <= body.clientWidth) return;
    body.scrollLeft += event.deltaX;
  };

  const isControlled = controlledPage !== undefined;
  const currentPage = isControlled ? controlledPage : internalPage;

  const isPageSizeControlled = controlledPageSize !== undefined;
  const pageSize = isPageSizeControlled ? controlledPageSize : Math.max(10, internalPageSize);

  const effectivelyLoading = loading === true || (loading !== false && data.length === 0);

  const handlePageSizeChange = (newPageSize: number) => {
    const validPageSize = Math.max(10, newPageSize);

    if (!isControlledSelection) {
      setSelectedRows(new Set());
      if (onSelectionChange) {
        onSelectionChange([]);
      }
    }

    if (!isPageSizeControlled) {
      setInternalPageSize(validPageSize);
    }

    if (!isControlled) {
      setInternalPage(1);
    }

    onPageSizeChange?.(validPageSize);
  };
  const handlePageChange = (page: number) => {
    if (!isControlled) {
      setInternalPage(page);
    }
    onPageChange?.(page);
    tableContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const totalCount = totalItems !== undefined ? totalItems : data.length;
  const effectiveShowPagination = showPagination !== false;

  const totalPages = useMemo(() => {
    const count = totalItems !== undefined ? totalItems : data.length;
    return Math.max(1, Math.ceil(count / pageSize));
  }, [data.length, pageSize, totalItems]);

  const paginatedData = useMemo(() => {
    if (!effectiveShowPagination) return data;

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    if (totalItems !== undefined) {
      return data;
    }

    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize, totalItems, effectiveShowPagination]);

  const emptyStateOnly = !effectivelyLoading && paginatedData.length === 0;

  const shouldVirtualize = virtualize && paginatedData.length > 30;
  const rowVirtualizer = useVirtualizer({
    count: shouldVirtualize ? paginatedData.length : 0,
    getScrollElement: () => virtualScrollRef.current,
    estimateSize: () => virtualRowHeight + 8, // row height + spacer
    overscan: 5,
  });

  const rowSelectable = (rowIndex: number): boolean =>
    !isRowSelectable || !!isRowSelectable(paginatedData[rowIndex], rowIndex);

  const isRowChecked = (rowIndex: number): boolean => {
    if (isControlledSelection) {
      if (!getRowId) return false;
      return selectedIdSet.has(getRowId(paginatedData[rowIndex], rowIndex));
    }
    return selectedRows.has(rowIndex);
  };

  const currentPageSelectableRows = () =>
    paginatedData.filter((_, index) => rowSelectable(index));

  const handleRowSelect = (index: number, checked: boolean) => {
    if (!selectable) return;
    if (!rowSelectable(index)) return;

    if (isControlledSelection) {
      if (onSelectionChange && getRowId) {
        const toggledId = getRowId(paginatedData[index], index);
        const selected = currentPageSelectableRows().filter((row, i) => {
          const id = getRowId(row, i);
          if (id === toggledId) return checked;
          return selectedIdSet.has(id);
        });
        onSelectionChange(selected);
      }
      return;
    }

    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      newSelectedRows.add(index);
    } else {
      newSelectedRows.delete(index);
    }
    setSelectedRows(newSelectedRows);

    if (onSelectionChange) {
      const selected = Array.from(newSelectedRows)
        .map((idx) => {
          const actualIdx = effectiveShowPagination ? idx - (currentPage - 1) * pageSize : idx;
          return paginatedData[actualIdx];
        })
        .filter(Boolean);
      onSelectionChange(selected);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (!selectable) return;

    if (isControlledSelection) {
      if (onSelectionChange) {
        onSelectionChange(checked ? currentPageSelectableRows() : []);
      }
      return;
    }

    const newSelectedRows = new Set<number>();
    if (checked) {
      paginatedData.forEach((_, index) => {
        if (rowSelectable(index)) newSelectedRows.add(index);
      });
    }
    setSelectedRows(newSelectedRows);

    if (onSelectionChange) {
      onSelectionChange(checked ? paginatedData.filter((_, index) => rowSelectable(index)) : []);
    }
  };

  const getCellValue = (column: Column<T>, row: T, index: number): React.ReactNode => {
    if (column.render) {
      return column.render(
        column.accessor
          ? typeof column.accessor === 'function'
            ? column.accessor(row)
            : row[column.accessor]
          : undefined,
        row,
        index
      );
    }

    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }

    if (column.accessor) {
      return row[column.accessor] ?? '';
    }

    return '';
  };

  const selectableIndices = paginatedData
    .map((_, index) => index)
    .filter((index) => rowSelectable(index));
  const allSelected = selectableIndices.length > 0 && selectableIndices.every((index) => isRowChecked(index));
  const someSelected = selectableIndices.some((index) => isRowChecked(index)) && !allSelected;

  const scrollbarClass = scrollbars === 'visible' ? 'table-scroll' : 'scrollbar-hide';
  const stickyHeaderClass = stickyHeader ? '[&>div:first-child]:overflow-visible' : '';
  const bodyXOverflowClass = proxyScrollActive ? 'overflow-x-hidden' : '';
  const bodyWrapperClasses = innerScroll
    ? bodyFillsParent
      ? cn(
          'overflow-x-auto flex-1 min-h-[240px] bg-transparent overflow-y-auto sm:px-0',
          scrollbarClass,
          stickyHeaderClass,
          bodyXOverflowClass,
        )
      : fillParent
      ? cn(
          'overflow-x-auto min-h-0 flex-1 bg-transparent overflow-y-auto sm:px-0',
          scrollbarClass,
          stickyHeaderClass,
          bodyXOverflowClass,
        )
      : cn(
          'overflow-x-auto max-h-[calc(100vh-300px)] bg-transparent overflow-y-auto sm:px-0',
          scrollbarClass,
          stickyHeaderClass,
          bodyXOverflowClass,
        )
    : cn('overflow-x-auto bg-transparent sm:px-0', scrollbarClass, stickyHeaderClass, bodyXOverflowClass);

  const thClass = isCardTheme
    ? 'text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[#98A2B3] px-3.5 py-3 whitespace-nowrap bg-[#FAFAFB] border-0'
    : 'font-medium text-xs sm:text-sm text-[#08090A] border-none bg-gray-200';
  const tdClass = isCardTheme
    ? 'px-3.5 py-3.5 align-middle text-[13px] text-[#101828] border-0'
    : 'whitespace-nowrap py-2 sm:py-3 md:py-4 px-2 sm:px-3 md:px-4 border-0 text-xs sm:text-sm';
  const rowClass = isCardTheme
    ? cn(
        'group border-b border-[#F2F4F7] last:border-b-0 transition hover:bg-[#F9FAFB]',
        onRowClick && 'cursor-pointer [&>td]:cursor-pointer',
      )
    : cn('group border-0', onRowClick && 'cursor-pointer [&>td]:cursor-pointer');
  const stickySelectTd = isCardTheme
    ? CARD_THEME_STICKY_SELECT_TD
    : 'sticky left-0 whitespace-nowrap z-10 py-2 sm:py-3 md:py-4 pl-2 sm:pl-3 md:pl-4 border-0 bg-white transition-colors group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,white)] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]';
  const stickyFirstOffset = selectable ? 'left-12' : 'left-0';
  const stickyFirstTd = isCardTheme
    ? cn(CARD_THEME_STICKY_FIRST_TD, stickyFirstOffset)
    : cn(
        'sticky z-10 bg-white transition-colors group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,white)] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]',
        stickyFirstOffset,
      );
  const stickyFirstTh = isCardTheme
    ? cn(CARD_THEME_STICKY_FIRST_TH, stickyFirstOffset)
    : cn('sticky z-30 bg-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]', stickyFirstOffset);

  const stickyActionsTd = isCardTheme
    ? CARD_THEME_STICKY_ACTIONS_TD
    : 'sticky right-0 z-10 bg-white text-right transition-colors group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,white)] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]';

  const filteredPageSizeOptions = pageSizeOptions.filter((size) => size >= 10);
  const paginationFooter = effectiveShowPagination ? (
    <ContactsPagination
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalItems ?? data.length}
      pageSize={pageSize}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      pageSizeOptions={filteredPageSizeOptions}
      isLoading={effectivelyLoading}
      itemLabel={paginationItemLabel}
      pageWindow={paginationWindow}
      hidePageSizeSelector={filteredPageSizeOptions.length <= 1}
      alwaysShow={alwaysShowPagination}
      testId="data-table-pagination"
      pageSizeTestId="data-table-page-size"
      className={cn('bg-white', !isCardTheme && 'rounded-b-lg')}
    />
  ) : null;

  if (isMobile && renderMobileCard) {
    return (
      <div className={cn('w-full space-y-3', className)} data-testid="data-table-mobile-cards">
        {effectivelyLoading ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="flex flex-col gap-3 rounded-2xl border border-[#EAECF0] bg-white p-4">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
              <div className="grid grid-cols-3 gap-2 border-t border-[#F2F4F7] pt-3">
                {[0, 1, 2].map((j) => (
                  <Skeleton key={j} className="h-8" />
                ))}
              </div>
            </div>
          ))
        ) : !paginatedData.length ? (
          <div
            className={cn(
              'flex min-h-[6rem] items-center justify-center rounded-2xl border border-[#EAECF0] bg-white px-4 py-6 text-center',
              isCardTheme ? 'text-[13px] text-[#98A2B3]' : 'text-muted-foreground',
            )}
          >
            {emptyMessage}
          </div>
        ) : (
          paginatedData.map((row, index) => {
            const actions = rowActions ? rowActions(row, index) : null;
            return (
              <React.Fragment key={getRowId ? getRowId(row, index) : index}>
                {renderMobileCard(row, {
                  index,
                  selectable: selectable && rowSelectable(index),
                  selected: selectable && isRowChecked(index),
                  onSelectedChange: (checked) => handleRowSelect(index, checked),
                  actionsMenu:
                    actions && actions.some((a) => !a.hidden) ? (
                      <RowActionsCell actions={actions} menuStyle={rowActionsMenuStyle} />
                    ) : null,
                  onClick: onRowClick ? () => onRowClick(row, index) : undefined,
                  cell: (key) => {
                    const column = columns.find((c) => c.key === key);
                    return column ? getCellValue(column, row, index) : null;
                  },
                })}
              </React.Fragment>
            );
          })
        )}
        {paginationFooter ? React.cloneElement(paginationFooter, { className: 'px-1' }) : null}
      </div>
    );
  }

  return (
    <div
      ref={tableContainerRef}
      className={cn(
        'w-full scroll-mt-28',
        'overflow-x-auto bg-transparent shadow-none',
        surface === 'card' &&
          'rounded-2xl border border-[#EAECF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]',
        bodyFillsParent && 'flex min-h-0 flex-1 flex-col',
        fillParent && 'flex h-full min-h-0 flex-col',
        className,
      )}
    >
      <div
        className={cn(
          'bg-transparent overflow-hidden w-full',
          bodyFillsParent && 'flex min-h-0 flex-1 flex-col',
          !isCardTheme && 'rounded-t-lg',
          fillParent && 'flex min-h-0 flex-1 flex-col',
        )}
      >
        <div
          ref={virtualScrollRef}
          className={bodyWrapperClasses}
          onScroll={proxyScrollActive ? handleBodyScrollSync : undefined}
          onWheel={proxyScrollActive ? handleBodyWheelSync : undefined}
        >
          <Table
            className={cn(
              'w-full border-0 min-w-max',
              emptyStateOnly && 'min-w-0 [&_th]:min-w-0',
              isCardTheme && 'text-[13px]',
            )}
          >
            <TableHeader
              className={cn(
                'sticky top-0 z-20',
                isCardTheme ? 'bg-[#FAFAFB] border-b border-[#EAECF0]' : 'bg-gray-400',
              )}
            >
              <TableRow
                className={cn(
                  isCardTheme
                    ? 'border-b border-[#EAECF0] bg-[#FAFAFB] hover:bg-[#FAFAFB]'
                    : 'border-none bg-gray-200 hover:bg-gray-200 data-[state=selected]:bg-gray-400',
                )}
              >
                {selectable && (
                  <TableHead
                    className={cn(
                      isCardTheme
                        ? CARD_THEME_STICKY_SELECT_TH
                        : 'w-12 sticky left-0 z-30 border-0 bg-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]',
                    )}
                  >
                    <Checkbox
                      checked={allSelected || (someSelected ? "indeterminate" : false)}
                      onCheckedChange={(checked) => handleSelectAll(!!checked)}
                      className="bg-white"
                      disabled={effectivelyLoading}
                    />
                  </TableHead>
                )}
                {columns.map((column, colIdx) => {
                  const isActionsColumn = column.key === 'actions';
                  return (
                    <TableHead
                      key={column.key}
                      className={cn(
                        thClass,
                        column.headerClassName,
                        stickyFirstColumn && colIdx === 0 && !isActionsColumn && stickyFirstTh,
                        isActionsColumn && (isCardTheme
                          ? CARD_THEME_STICKY_ACTIONS_TH
                          : 'sticky right-0 z-30 bg-gray-200 text-right shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]'),
                      )}
                    >
                      {column.header}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {effectivelyLoading ? (
                Array.from({ length: pageSize || 10 }).map((_, rowIndex) => (
                  <React.Fragment key={`skeleton-row-${rowIndex}`}>
                    <TableRow className={isCardTheme ? 'border-b border-[#F2F4F7] last:border-b-0' : 'border-0'}>
                      {selectable && (
                        <TableCell className={stickySelectTd}>
                          <Skeleton className="h-4 w-4 rounded bg-white/50" />
                        </TableCell>
                      )}
                      {columns.map((column, colIdx) => {
                        const isActionsColumn = column.key === 'actions';
                        const isFirst = !selectable && colIdx === 0;
                        const isLast = colIdx === columns.length - 1;
                        return (
                          <TableCell
                            key={`skeleton-cell-${rowIndex}-${column.key}`}
                            className={cn(
                              tdClass,
                              stickyFirstColumn && colIdx === 0 && !isActionsColumn && stickyFirstTd,
                              isActionsColumn && stickyActionsTd,
                              !isCardTheme && isFirst && 'rounded-l-[12px]',
                              !isCardTheme && isLast && !isActionsColumn && 'rounded-r-[12px]',
                            )}
                          >
                            <Skeleton className={cn(
                              "h-4 rounded",
                              isActionsColumn ? "w-8 mx-auto" : "w-full"
                            )} />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {!isCardTheme && rowIndex !== (pageSize || 10) - 1 && (
                      <TableRow key={`skeleton-spacer-${rowIndex}`} className="border-0">
                        <TableCell colSpan={columns.length + (selectable ? 1 : 0)} className="p-0 border-0">
                          <div style={{ height: 8 }}></div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              ) :
                paginatedData.length === 0 ? (
                  <TableRow className="border-0">
                    <TableCell
                      colSpan={columns.length + (selectable ? 1 : 0)}
                      className="p-0 border-0"
                    />
                  </TableRow>
                ) : shouldVirtualize ? (
                  <>
                    {rowVirtualizer.getVirtualItems().length > 0 && (
                      <tr style={{ height: rowVirtualizer.getVirtualItems()[0]?.start ?? 0 }}>
                        <td colSpan={columns.length + (selectable ? 1 : 0)} className="p-0 border-0" />
                      </tr>
                    )}
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const rowIndex = virtualRow.index;
                      const row = paginatedData[rowIndex];
                      const actualIndex = showPagination ? (currentPage - 1) * pageSize + rowIndex : rowIndex;
                      const isSelected = isRowChecked(rowIndex);

                      return (
                        <React.Fragment key={`vrow-${actualIndex}`}>
                          <TableRow
                            className={rowClass}
                            onClick={(e) => {
                              if ((e.target as HTMLElement).closest('[data-actions-cell]')) return
                              onRowClick?.(row, actualIndex)
                            }}
                          >
                            {selectable && (
                              <TableCell className={stickySelectTd}>
                                <Checkbox
                                  checked={isSelected}
                                  disabled={!rowSelectable(rowIndex)}
                                  onCheckedChange={(checked) => handleRowSelect(rowIndex, !!checked)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="bg-white"
                                />
                              </TableCell>
                            )}
                            {columns.map((column, colIdx) => {
                              const isActionsColumn = column.key === 'actions';
                              return (
                                <TableCell
                                  key={column.key}
                                  data-actions-cell={isActionsColumn || undefined}
                                  className={cn(
                                    tdClass,
                                    column.className,
                                    stickyFirstColumn && colIdx === 0 && !isActionsColumn && stickyFirstTd,
                                    isActionsColumn && stickyActionsTd,
                                  )}
                                >
                                  {getCellValue(column, row, actualIndex)}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                          {!isCardTheme && rowIndex !== paginatedData.length - 1 && (
                            <TableRow className="border-0">
                              <TableCell colSpan={columns.length + (selectable ? 1 : 0)} className="p-0 border-0">
                                <div style={{ height: 8 }}></div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {rowVirtualizer.getVirtualItems().length > 0 && (
                      <tr style={{ height: rowVirtualizer.getTotalSize() - (rowVirtualizer.getVirtualItems().at(-1)?.end ?? 0) }}>
                        <td colSpan={columns.length + (selectable ? 1 : 0)} className="p-0 border-0" />
                      </tr>
                    )}
                  </>
                ) : (
                  paginatedData.map((row, rowIndex) => {
                    const actualIndex = showPagination ? (currentPage - 1) * pageSize + rowIndex : rowIndex;
                    const isSelected = isRowChecked(rowIndex);

                    return (
                      <React.Fragment key={`row-fragment-${actualIndex}`}>
                        <TableRow
                          className={rowClass}
                          onClick={(e) => {
                            if ((e.target as HTMLElement).closest('[data-actions-cell]')) return
                            onRowClick?.(row, actualIndex)
                          }}
                        >
                          {selectable && (
                            <TableCell className={stickySelectTd}>
                              <Checkbox
                                checked={isSelected}
                                disabled={!rowSelectable(rowIndex)}
                                onCheckedChange={(checked) => {
                                  handleRowSelect(rowIndex, !!checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white"
                              />
                            </TableCell>
                          )}
                          {columns.map((column, colIdx) => {
                            const isActionsColumn = column.key === 'actions';
                            const isFirst = !selectable && colIdx === 0;
                            const isLast = colIdx === columns.length - 1;
                            return (
                              <TableCell
                                key={column.key}
                                data-actions-cell={isActionsColumn || undefined}
                                className={cn(
                                  tdClass,
                                  column.className,
                                  stickyFirstColumn && colIdx === 0 && !isActionsColumn && stickyFirstTd,
                                  isActionsColumn && stickyActionsTd,
                                  !isCardTheme && isFirst && 'rounded-l-[12px]',
                                  !isCardTheme && isLast && !isActionsColumn && 'rounded-r-[12px]',
                                )}
                              >
                                {getCellValue(column, row, actualIndex)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        {!isCardTheme && rowIndex !== paginatedData.length - 1 && (
                          <TableRow key={`spacer-${actualIndex}`} className="border-0">
                            <TableCell colSpan={columns.length + (selectable ? 1 : 0)} className="p-0 border-0">
                              <div style={{ height: 8 }}></div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
            </TableBody>
          </Table>
          {emptyStateOnly && (
            <div className="sticky left-0 w-full">
              <div
                className={cn(
                  'flex min-h-[6rem] items-center justify-center px-4 text-center',
                  isCardTheme ? 'text-[#98A2B3] text-[13px]' : 'text-muted-foreground',
                )}
              >
                {emptyMessage}
              </div>
            </div>
          )}
          {proxyScrollActive && proxyScrollWidth > 0 && proxyScrollOverflows && (
            <div
              ref={proxyScrollRef}
              onScroll={handleProxyScrollSync}
              aria-hidden="true"
              className={cn(
                'sticky left-0 w-full overflow-x-auto overflow-y-hidden',
                scrollbarClass,
              )}
            >
              <div style={{ width: proxyScrollWidth, height: 1 }} />
            </div>
          )}
          {paginationInScroll && (
            <div className="sticky left-0 w-full">{paginationFooter}</div>
          )}
        </div>
      </div>

      {!paginationInScroll && paginationFooter}
    </div>
  );
}

export default DataTable;
